import { z } from "zod";

/**
 * Zod schema for per-entity Ship & Debit configuration.
 * Stored in entity_module_config.config JSONB when module = "ship-and-debit".
 */
export const ShipAndDebitEntityConfigSchema = z.object({
  sd_844_delivery_method: z.enum(["edi", "email"]).optional(),
  sd_849_response_method: z.enum(["edi", "email"]).optional(),
  sd_844_email_addresses: z.array(z.string().email()).optional(),
  sd_849_email_addresses: z.array(z.string().email()).optional(),
  sd_trading_partner_names: z.array(z.string().min(1)).optional(),
  /** Learned aliases for 849 supplier matching — names from EDI N1*SE or Excel
   *  supplier configs that didn't match the Supplier record name. Populated
   *  automatically when a user assigns an unmatched 849 response to a supplier. */
  sd_849_supplier_aliases: z.array(z.string().min(1)).optional(),
  vendor_number: z.string().optional(),
  /** Equivalent vendor numbers for a single supplier (e.g. ABB has 4 active
   *  AS/400 vendor accounts). Used for BPID matching (try every number, stop
   *  at first ECPBPID hit) and to expand the VDM supplier dropdown into one
   *  entry per vendor number. Sorted ascending at runtime. `vendor_number`
   *  remains as a single-value fallback for legacy reads. */
  vendor_numbers: z.array(z.string().min(1)).optional(),
  vdm_cc_email: z.string().optional(),
  sd_844_resubmission_enabled: z.boolean().optional(),
  /** When true, enables the 849-rejection resubmission flow for this supplier
   *  (Schneider, ABB). On the 849 detail page, disagreeing with a rejected or
   *  partial line marks the matched 844 transaction for editing and lets the
   *  user resubmit it as a NEW 844 submission with a new batch number, linked
   *  back to the original submission and the originating 849. EDI suppliers
   *  only. Distinct from `sd_844_resubmission_enabled` (the pre-849 failed-
   *  submission retry flow). */
  sd_849_resubmission_enabled: z.boolean().optional(),
  /** When true, the 849 Rejection Investigation agent runs for this supplier
   *  (both auto-trigger from workflow and manual trigger from the UI). */
  sd_849_investigation_enabled: z.boolean().optional(),
  /** General investigation guidelines for this supplier — applies to ALL
   *  rejection codes. Free-text context the agent reads before investigating. */
  sd_849_investigation_general_notes: z.string().optional(),
  /** Per-rejection-code investigation context for the 849 Rejection Investigation
   *  agent. Keyed by X12 rejection code (e.g., "26"). Each entry describes what
   *  the code means for THIS supplier, the ordered investigation steps the agent
   *  must follow, known causes, and fixes. Configured by users from the S&D Agent
   *  Config admin page; takes priority over hardcoded defaults. */
  sd_849_investigation_context: z
    .record(
      z.string(),
      z.object({
        meaning: z.string().min(1),
        investigation_steps: z.array(z.string().min(1)),
        common_causes: z.array(z.string()),
        common_fixes: z.array(z.string()),
        additional_notes: z.string().default(""),
      }),
    )
    .optional(),
  /** Free-text context that steers the LLM fallback which interprets inbound
   *  email 849 responses (subject + body + attachments) into structured intent
   *  when deterministic parsing can't. Read on TWO levels and composed together:
   *  the org-level row (entityType="organization") holds context shared across
   *  ALL email suppliers; the per-company row holds supplier-specific additions
   *  (e.g. "the 'Reference: E#####' is our batch; ignore the 'Batch ID #'; the
   *  summary VDM appears as 'summary of VDM NNNNN'; zero-variance batches have no
   *  Excel"). The LLM only proposes structure — the deterministic reconcile +
   *  validate-against-claimed guard still decides what is accepted. */
  sd_849_email_context: z.string().optional(),
  /** When true, the email-849 matching agent runs for this supplier (deterministic
   *  first, LLM agent fallback). EDI suppliers ignore this. Default off. */
  sd_849_email_matching_enabled: z.boolean().optional(),
  sd_844_include_vdm_number: z.boolean().optional(),
  sd_844_submission_rules: z
    .array(
      z.object({
        name: z.string().min(1),
        type: z.enum(["include", "exclude"]).default("exclude"),
        pipeline: z.enum(["batch", "vdm", "all"]).default("all"),
        logic: z.enum(["and", "or"]).default("and"),
        conditions: z.array(
          z.object({
            field: z.string().min(1),
            operator: z.string().min(1),
            value: z.unknown().optional(),
          }),
        ),
      }),
    )
    .optional(),
});

/** Per-entity module configuration for Ship & Debit — derived from Zod schema. */
export type ShipAndDebitEntityConfig = z.infer<
  typeof ShipAndDebitEntityConfigSchema
>;

export type SubmissionRuleCondition = {
  field: string;
  operator: string;
  value?: unknown;
};

export type SubmissionRule = {
  name: string;
  type: "include" | "exclude";
  pipeline: "batch" | "vdm" | "all";
  logic: "and" | "or";
  conditions: SubmissionRuleCondition[];
};

/**
 * Union type for all per-entity module configs.
 * Extend with other module config types as needed.
 */
export type EntityModuleConfigMap = {
  "ship-and-debit"?: ShipAndDebitEntityConfig;
};
